"use client";
/**
 * Everything the catalog page could not carry, opening with the thing the
 * second call was billed for.
 *
 * The demonstration leads, at a frame capped to 533 pixels. That number is
 * derived, not chosen: the clip and its poster measure 1280×720, and the kit's
 * own no-upscale ceiling for a shape-bearing frame is 300 pixels of height at
 * `Hero`, so 300 × 1280 ÷ 720 = 533 is the widest frame that reaches the
 * ceiling without crossing it. A full-width 556 frame would be clamped to 300
 * and stamped `contained`, which is 23 pixels of bare plate on each side of a
 * picture that could have filled the box.
 *
 * Below it, in order: the movement in the selected register, the vendor's own
 * coaching points, and the record's labelled facts. The two pre-signed
 * addresses are offered as evidence ONLY while the signature is live — a dead
 * link presented as evidence is not evidence.
 */
import type { CSSProperties, ReactElement } from "react";
import { IconBarbell } from "@tabler/icons-react";
import {
  CresultcardAction,
  CresultcardDetail,
  CresultcardFactGroup,
  CresultcardIdentity,
  CresultcardListRow,
  CresultcardProvenance,
  CresultcardSectionHeader,
  CresultcardStack,
  CRC_TEXT_FAINT,
  CRC_TYPE_STYLE,
  CresultcardActionTone,
  CresultcardFactGroupShape,
  CresultcardFactWeight,
  CresultcardIdentitySlot,
  CresultcardMediaScale,
  CresultcardStackDensity,
  CresultcardTextStep,
  CresultcardTitleLines,
  useCresultcardVariant,
  type CresultcardFactEntry,
} from "@/components/ui/Cresultcard";
import {
  CWORKOUTCARD_DEMONSTRATION_TITLE,
  CWORKOUTCARD_SIGNED_UNTIL_LABEL,
  CWORKOUTCARD_EXERCISE_LABEL,
  CWORKOUTCARD_POINTS_TITLE,
  CWORKOUTCARD_STEPS_TITLE,
  C_WORKOUT_DETAIL_MEDIA_WIDTH,
  C_WORKOUT_MOVE_READINGS,
  C_WORKOUT_MOVE_READING_GROUP,
} from "./cworkoutcard.constants";
import {
  cworkoutcardIsPlayable,
  cworkoutcardPrimaryClip,
  cworkoutcardVendorWords,
} from "./cworkoutcard.model";
import {
  CworkoutcardMetaLine,
  CworkoutcardMove,
  CworkoutcardTypeChips,
  CworkoutcardVideoCaption,
} from "./CworkoutcardMeta";
import {
  CworkoutcardVideo,
  useCworkoutcardVideoState,
} from "./CworkoutcardVideo";
import {
  type CworkoutcardData,
  type CworkoutcardDetailProps,
} from "./cworkoutcard.types";

/** Nothing yields here: the title is unclamped and the fact line measures itself. */
const DETAIL_TRUNCATION: readonly CresultcardIdentitySlot[] = [];
const SUMMARY_STYLE: CSSProperties = {
  ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
  flex: "0 0 auto",
  color: CRC_TEXT_FAINT,
};

const MP4_LABEL = "MP4 source";
const HLS_LABEL = "HLS playlist";

/** Every labelled cell the pane draws, and only the ones this record filled. */
function detailFacts(data: CworkoutcardData): readonly CresultcardFactEntry[] {
  return [
    {
      label: "Muscle",
      value: cworkoutcardVendorWords(data.muscleGroup),
      weight: CresultcardFactWeight.Short,
    },
    {
      label: "Equipment",
      value: cworkoutcardVendorWords(data.equipment),
      weight: CresultcardFactWeight.Short,
    },
    {
      label: "Difficulty",
      value: cworkoutcardVendorWords(data.difficulty),
      weight: CresultcardFactWeight.Short,
    },
    ...(data.category == null
      ? []
      : [
          {
            label: "Category",
            value: cworkoutcardVendorWords(data.category),
            weight: CresultcardFactWeight.Short,
          },
        ]),
    ...(data.secondaryMuscles.length === 0
      ? []
      : [
          {
            label: "Also works",
            value: data.secondaryMuscles
              .map(cworkoutcardVendorWords)
              .join(", "),
            weight: CresultcardFactWeight.Long,
          },
        ]),
  ];
}

export function CworkoutcardDetail({
  data,
  context,
  bounds,
  provenance,
  moveReading,
  frame,
}: CworkoutcardDetailProps): ReactElement {
  const { value: registryReading } = useCresultcardVariant({
    group: C_WORKOUT_MOVE_READING_GROUP,
    options: C_WORKOUT_MOVE_READINGS,
  });
  const state = useCworkoutcardVideoState(data);
  const clip = cworkoutcardPrimaryClip(data.media);
  const evidenceLive = cworkoutcardIsPlayable(state) && clip != null;

  return (
    <CresultcardDetail
      bounds={bounds}
      context={context}
      frame={frame}
      icon={IconBarbell}
      label={CWORKOUTCARD_EXERCISE_LABEL}
      provenance={
        <CresultcardProvenance
          primary={provenance}
        />
      }
    >
      <CresultcardStack
        density={CresultcardStackDensity.Regular}
      >
        <span
          data-el="Cworkoutcard/Detail/Demonstration"
          // The cap yields to the pane. This detail is also a set's drill, and
          // in the Cards layout that pane is the card's own cell.
          style={{ display: "block", width: C_WORKOUT_DETAIL_MEDIA_WIDTH }}
        >
          <CresultcardStack
            density={CresultcardStackDensity.Snug}
          >
            <CworkoutcardVideo
              data={data}
              scale={CresultcardMediaScale.Hero}
            />
            <CworkoutcardVideoCaption
              data={data}
              state={state}
            />
          </CresultcardStack>
        </span>

        <CresultcardIdentity
          secondary={
            <CworkoutcardMetaLine
              data={data}
            />
          }
          title={data.title}
          titleLines={CresultcardTitleLines.Full}
          truncationOrder={DETAIL_TRUNCATION}
        />

        <CworkoutcardTypeChips
          data={data}
        />

        <CresultcardStack
          density={CresultcardStackDensity.Snug}
        >
          <CresultcardSectionHeader
            title={CWORKOUTCARD_STEPS_TITLE}
          />
          <CworkoutcardMove
            data={data}
            reading={moveReading ?? registryReading}
          />
        </CresultcardStack>

        {data.importantPoints.length > 0 && (
          <CresultcardStack
            density={CresultcardStackDensity.Snug}
          >
            <CresultcardSectionHeader
              title={CWORKOUTCARD_POINTS_TITLE}
            />
            {data.importantPoints.map((point, index) => (
              <CresultcardListRow
                key={point}
                onLast={index === data.importantPoints.length - 1}
                title={point}
                titleLines={CresultcardTitleLines.Two}
              />
            ))}
          </CresultcardStack>
        )}

        <CresultcardFactGroup
          entries={detailFacts(data)}
          group={CresultcardFactGroupShape.Column}
        />

        {evidenceLive && (
          <CresultcardStack
            density={CresultcardStackDensity.Snug}
          >
            <CresultcardSectionHeader
              title={CWORKOUTCARD_DEMONSTRATION_TITLE}
              trailing={
                data.media?.expiresAt == null ? undefined : (
                  <span
                    style={SUMMARY_STYLE}
                  >
                    {`${CWORKOUTCARD_SIGNED_UNTIL_LABEL} ${data.media.expiresAt}`}
                  </span>
                )
              }
            />
            <CresultcardAction
              compact
              href={clip.videoUrl}
              label={MP4_LABEL}
              tone={CresultcardActionTone.Quiet}
            />
            {clip.videoHlsUrl != null && (
              <CresultcardAction
                compact
                href={clip.videoHlsUrl}
                label={HLS_LABEL}
                tone={CresultcardActionTone.Quiet}
              />
            )}
          </CresultcardStack>
        )}
      </CresultcardStack>
    </CresultcardDetail>
  );
}
